import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useService } from '@aesop-fables/containr-react';
import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import { ToastbarContext } from '../../App';
import {
  clearRevShareTransactions as clearTransactions,
  fetchRevShareTransactions as fetchTransactions,
  getCachedRevShareTransactions as getCachedTransactions,
} from '../../data/rev-share';
import { useLoading } from '../../hooks/useLoading';
import { useCommands } from '../../helpers/useCommands';
import {
  AnyTransactionsData,
  OrgTransactionsData,
  TeamTransactionsData,
  GroupTransactionsData,
} from '../../models/RevShareData';
import { Toast } from '../../models/Toast';
import { GroupData } from '../../models/GroupData';
import { TeamData } from '../../models/TeamData';
import { OrganizationData } from '../../models/OrganizationData';
import { DrillDownWizard, drillDownWizardKey, AdminTierTypeEnum } from '../../services/drillDown';
import { GroupCompartments, groupStorageKey } from '../../data/group';
import { TeamCompartments, teamStorageKey } from '../../data/team';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import GroupTransactionsTable from './tables/GroupTransactionsTable';
import OrgTransactionsTable from './tables/OrgTransactionsTable';
import TeamTransactionsTable from './tables/TeamTransactionsTable';
import {
  TransactionsDateRange,
  DateRangeType,
  sortTransactionsData,
} from '../../data/rev-share/helpers/transactionsUtils';
import {
  GenerateOrgRevSharePdf,
  GenerateTeamRevSharePdf,
  GenerateGroupRevSharePdf,
} from '../../data/rev-share/commands';
import { buildPdfFilename } from '../../data/rev-share/helpers/transactionsUtils';

export interface TransactionsTableProps {
  startDate: string;
  endDate: string;
  onExportPdf: () => Promise<void>;
  emptyStateText: string;
  loading: boolean;
}

interface TransactionsProps {
  dateRange: TransactionsDateRange;
}

const Transactions: React.FC<TransactionsProps> = ({ dateRange }) => {
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const appStorage = useAppStorage();
  const commands = useCommands();
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const tier = useObservable(drillDownWizard.tier$);
  const [data, setData] = useState<AnyTransactionsData | undefined>(undefined);

  const groupDataCache = appStorage.retrieve<GroupCompartments>(groupStorageKey);
  const teamDataCache = appStorage.retrieve<TeamCompartments>(teamStorageKey);
  const orgDataCache = appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const groupData = useObservable(groupDataCache.observe$<GroupData>('group'));
  const teamData = useObservable(teamDataCache.observe$<TeamData>('team'));
  const orgData = useObservable(orgDataCache.observe$<OrganizationData>('organization'));
  const { startDate, endDate, rangeType } = dateRange;

  const isEmptyCustomRange = rangeType === DateRangeType.Custom && (!startDate || !endDate);

  const emptyStateText = isEmptyCustomRange
    ? 'Please choose a start and end date.'
    : 'No transaction data available.';

  useEffect(() => {
    if (!tier) return;
    clearTransactions(appStorage);
  }, [tier, appStorage]);

  useEffect(() => {
    if (!tier) return;

    const cached = getCachedTransactions(appStorage, dateRange, tier);
    if (cached) {
      const sorted = sortTransactionsData(cached, tier);
      setData(sorted);
    } else {
      setData(undefined);
    }

    const load = async () => {
      if (!dateRange.startDate || !dateRange.endDate) return;

      try {
        const res = await fetchTransactions(appStorage, dateRange, tier);

        if (res) {
          const sorted = sortTransactionsData(res, tier);
          if (!cached || JSON.stringify(sorted) !== JSON.stringify(cached)) {
            setData(sorted);
          }
        }
      } catch (e) {
        console.error('Error loading transactions:', e);
        setToast(
          new Toast({
            message: 'Error loading transactions',
            severity: 'error',
            open: true,
          }),
        );
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, dateRange]);

  const getEntityName = (currentTier: AdminTierTypeEnum): string => {
    switch (currentTier) {
      case AdminTierTypeEnum.Root:
        return orgData?.name ?? '';
      case AdminTierTypeEnum.Organization:
        return teamData?.name ?? '';
      case AdminTierTypeEnum.Team:
        return groupData?.name ?? drillDownWizard.findGroupNameById();
      default:
        return '';
    }
  };

  const handleExportPdf = useCallback(async () => {
    if (!data || !tier) return;

    try {
      setLoading(true);
      // Ensure loading state appears before expensive PDF generation
      await new Promise(resolve => setTimeout(resolve, 0));

      let response;
      const dateRange = {
        startDate: startDate ?? '',
        endDate: endDate ?? '',
      };

      switch (tier) {
        case AdminTierTypeEnum.Root:
          response = await commands.execute(GenerateOrgRevSharePdf, dateRange);
          break;
        case AdminTierTypeEnum.Organization:
          response = await commands.execute(GenerateTeamRevSharePdf, dateRange);
          break;
        case AdminTierTypeEnum.Team:
          response = await commands.execute(GenerateGroupRevSharePdf, dateRange);
          break;
        default:
          throw new Error(`Unsupported tier: ${tier}`);
      }

      const entityName = getEntityName(tier);
      const filename = buildPdfFilename(entityName, startDate, endDate);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setToast(
        new Toast({
          message: 'PDF generated successfully!',
          severity: 'success',
          open: true,
        }),
      );
    } catch (err) {
      console.error('PDF generation failed:', err);
      setToast(
        new Toast({
          message: 'Error generating PDF',
          severity: 'error',
          open: true,
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tier, startDate, endDate, orgData, teamData, groupData]);

  const commonProps = {
    startDate: startDate ?? '',
    endDate: endDate ?? '',
    onExportPdf: handleExportPdf,
    emptyStateText,
    loading: Boolean(!data && startDate && endDate),
  };

  switch (tier) {
    case AdminTierTypeEnum.Root:
      return <OrgTransactionsTable data={data as OrgTransactionsData} {...commonProps} />;
    case AdminTierTypeEnum.Organization:
      return <TeamTransactionsTable data={data as TeamTransactionsData} {...commonProps} />;
    case AdminTierTypeEnum.Team:
      return <GroupTransactionsTable data={data as GroupTransactionsData} {...commonProps} />;
    default:
      return null;
  }
};

export default Transactions;
